"use client";

import { useMemo } from "react";
import Image from "../ui/OptimizedImage";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api";
import { queryKeys } from "../../lib/query-keys";
import { useDeferredLoading } from "../../hooks/useDeferredLoading";

type HostSummary = {
    id: string;
    name: string;
    imageUrl?: string | null;
    _count?: { events: number };
};

export function PopularHosts() {
    const { data, isLoading } = useQuery({
        queryKey: queryKeys.hosts.all,
        queryFn: () => apiFetch<HostSummary[]>("hosts"),
        staleTime: 5 * 60_000,
    });

    const hosts = useMemo(() => (data ?? []).slice(0, 10), [data]);

    const showSkeleton = useDeferredLoading(isLoading);

    if (isLoading && showSkeleton) {
        return (
            <section className="px-4 py-6 md:px-6" aria-busy="true" aria-label="Loading popular hosts">
                <div className="mx-auto max-w-6xl">
                    <h2 className="mb-4 text-sm font-semibold text-[var(--hopp-text)]">
                        Popular Hosts
                    </h2>
                    <div className="flex gap-4 overflow-hidden">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="flex flex-col items-center gap-2">
                                <div className="h-16 w-16 animate-pulse rounded-full bg-[var(--hopp-panel)]" />
                                <div className="h-3 w-12 animate-pulse rounded bg-[var(--hopp-panel)]" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (isLoading) {
        return null;
    }

    if (hosts.length === 0) return null;

    return (
        <section className="px-4 py-6 md:px-6">
            <div className="mx-auto max-w-6xl">
                {/* Section Header */}
                <h2 className="mb-4 text-sm font-semibold text-[var(--hopp-text)]">
                    Popular Hosts
                </h2>

                {/* Horizontal Scroll Container */}
                <div className="scrollbar-hide -mx-4 flex gap-4 overflow-x-auto px-4 pb-2">
                    {hosts.map((host) => (
                        <Link
                            key={host.id}
                            href={`/hosts/${host.id}`}
                            className="flex-shrink-0"
                        >
                            <div
                                className="group flex w-20 flex-col items-center gap-2 text-center"
                            >
                                {/* Avatar */}
                                <div className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-white/20 transition-all group-hover:border-white/40">
                                    {host.imageUrl ? (
                                        <Image
                                            src={host.imageUrl}
                                            alt={host.name}
                                            fill
                                            sizes="64px"
                                            className="object-cover"
                                        />
                                    ) : (
                                        <span className="text-xs font-semibold uppercase text-[var(--hopp-muted)]">
                                            {host.name.slice(0, 2)}
                                        </span>
                                    )}
                                </div>

                                {/* Name */}
                                <p className="line-clamp-2 text-[10px] font-medium text-[var(--hopp-muted)] transition-colors group-hover:text-[var(--hopp-text)]">
                                    {host.name}
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
